import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { Container } from 'react-bootstrap';
import { FaCreditCard, FaLock, FaTruck, FaUndoAlt } from 'react-icons/fa';
import {
    useGetSpotlight1Query,
    useGetSpotlight2Query,
    useGetSpotlight3Query,
    useGetNewArrivalsQuery,
    useGetActiveSalesQuery,
    useGetTopChoicesQuery,
    useGetProductTypesQuery,
} from '../slices/productsApiSlice';
import { Link } from 'react-router-dom';
import { pickHeroProducts } from '../utils/heroProducts';
import { getWelcomeOffer, offerHeadline } from '../utils/welcomeOffer';
import HeroWall from '../components/HeroWall';
import HeaderNav from '../components/HeaderNav';
import StayTop from '../components/StayTop';
import Meta from '../components/Meta';
import GiftFinder from '../components/GiftFinder';
import StickyNavFix from '../components/StickyNavFix';
import WelcomeOfferBanner from '../components/WelcomeOfferBanner';
import CollectionCarousel from '../components/CollectionCarousel';
import RDWidget from '../components/RDWidget';
import PaymentMethodsBand from '../components/PaymentMethodsBand';
import '../assets/styles/landing.css';

const TRUST_POINTS = [
    { Icon: FaTruck, title: 'Free delivery', text: 'On orders of £25+' },
    { Icon: FaUndoAlt, title: '14-day returns', text: 'Hassle-free' },
    { Icon: FaLock, title: 'Secure checkout', text: 'Secured by Stripe' },
    { Icon: FaCreditCard, title: 'Pay later', text: 'Klarna and Clearpay' },
];

const productLink = (product) => `/product/${product.slug || product._id}`;

const LandingPage = () => {
    // ========================================
    // ADD LANDING PAGE CLASS TO BODY
    // ========================================
    useEffect(() => {
        document.body.classList.add('landing-page');
        return () => {
            document.body.classList.remove('landing-page');
        };
    }, []);

    // ========================================
    // API DATA QUERIES - CLEAN & SIMPLE
    // ========================================
    const { data: spotlight1 } = useGetSpotlight1Query();
    const { data: spotlight2, isLoading: loadingSpotlight2 } = useGetSpotlight2Query();
    const { data: spotlight3, isLoading: loadingSpotlight3 } = useGetSpotlight3Query();
    const { data: newArrivals, isLoading: loadingNewArrivals } = useGetNewArrivalsQuery();
    const { data: activeSales, isLoading: loadingActiveSales } = useGetActiveSalesQuery();
    const { data: topChoices, isLoading: loadingTopChoices } = useGetTopChoicesQuery();
    const { data: productTypes } = useGetProductTypesQuery();

    // Wait for every list so the mix doesn't change while the shopper is looking at it
    const heroReady = !loadingNewArrivals && !loadingTopChoices && !loadingSpotlight2 && !loadingSpotlight3;
    const heroProducts = useMemo(() => {
        if (!heroReady) return [];
        // The featured product further down the page (spotlight 1) is left out
        const topPicks = [spotlight2, spotlight3, ...(topChoices || [])];
        return pickHeroProducts({ newArrivals: newArrivals || [], topPicks });
    }, [heroReady, newArrivals, topChoices, spotlight2, spotlight3]);

    // ========================================
    // STATE MANAGEMENT
    // ========================================
    const [offer, setOffer] = useState(null);
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        let active = true;
        getWelcomeOffer().then((result) => { if (active) setOffer(result); });
        return () => { active = false; };
    }, []);

    // ========================================
    // NEWSLETTER FORM HANDLER - UPDATED WITH MAILCHIMP
    // ========================================
    const handleNewsletterSubmit = async (e) => {
        e.preventDefault();

        // Prevent double submissions
        if (isSubmitting) return;

        setIsSubmitting(true);

        try {
            const { data } = await axios.post('/api/newsletter/subscribe', {
                email,
                source: 'landing_page'
            });

            // Success! Show fun retro message
            toast.success(data.message, {
                position: 'top-center',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });

            // Clear the email input
            setEmail('');

        } catch (error) {
            // Show error message
            const message = error.response?.data?.message || 'Oops! Something went wrong. Please try again.';

            toast.error(message, {
                position: 'top-center',
                autoClose: 5000,
            });

        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Meta title="Retro Gaming & Pop Culture Collectibles UK" />
            <StickyNavFix />
            <StayTop />
            <HeaderNav />

            <RDWidget onSend={async (message, history) => {
                const excludeIds = history
                    .filter((m) => m.role === 'rd' && m.products)
                    .flatMap((m) => m.products.map((p) => p.id));

                const res = await fetch('/api/agent/chat', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ message, history, excludeIds }),
                });
                return res.json();
            }} />

            <div className="rd-top">
            <WelcomeOfferBanner />

            <section className="rd-hero" aria-labelledby="rd-hero-title">
                <div className="rd-hero__inner">
                    {heroReady ? <HeroWall products={heroProducts} /> : <div className="rd-hero__placeholder" />}
                </div>
            </section>
            </div>

            <section className="rd-trust" aria-label="Why shop with us">
                <ul className="rd-trust__list">
                    {TRUST_POINTS.map(({ Icon, title, text }) => (
                        <li key={title}>
                            <Icon aria-hidden="true" />
                            <span><strong>{title}</strong><small>{text}</small></span>
                        </li>
                    ))}
                </ul>
            </section>

            <section className="rd-section">
                <Container>
                    <div className="rd-section-head"><h2>Find the Perfect Gift</h2></div>
                    <GiftFinder />
                </Container>
            </section>

            {productTypes?.length > 0 && (
                <section className="rd-section">
                    <Container>
                        <div className="rd-section-head"><h2>Shop by Category</h2></div>
                        <ul className="rd-categories">
                            {productTypes.filter((type) => type.image && type.productType).map((type) => (
                                <li key={type.productType}>
                                    <Link to={{ pathname: '/search/', search: `?productType=${encodeURIComponent(type.productType)}` }}>
                                        <img src={type.image} alt="" loading="lazy" />
                                        <span>{type.productType}</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </Container>
                </section>
            )}

            <CollectionCarousel
                title="New Arrivals"
                viewAllLink="/search?sort=newest"
                products={newArrivals}
                isLoading={loadingNewArrivals}
            />

            {activeSales?.length > 0 && (
                <CollectionCarousel
                    title="On Sale Now"
                    viewAllLink="/search?onSale=true"
                    products={activeSales}
                    isLoading={loadingActiveSales}
                />
            )}

            <CollectionCarousel
                title="Our Top Picks"
                viewAllLink="/search/"
                products={topChoices}
                isLoading={loadingTopChoices}
            />

            {spotlight1 && (
                <section className="rd-section">
                    <Container>
                        <div className="rd-feature">
                            <Link to={productLink(spotlight1)} className="rd-feature__image">
                                <img src={spotlight1.image} alt={spotlight1.name} loading="lazy" />
                            </Link>
                            <div className="rd-feature__copy">
                                <p className="rd-feature__eyebrow">Featured this week</p>
                                <h2>{spotlight1.name}</h2>
                                {spotlight1.description && (
                                    <p className="rd-feature__text">{spotlight1.description.replace(/\\n/g, ' ')}</p>
                                )}
                                <p className="rd-feature__price">£{Number(spotlight1.price).toFixed(2)}</p>
                                <Link to={productLink(spotlight1)} className="rd-btn rd-btn--primary">View Product</Link>
                            </div>
                        </div>
                    </Container>
                </section>
            )}

            <section className="rd-section">
                <Container>
                    <PaymentMethodsBand />
                </Container>
            </section>

            <section className="rd-section rd-section--tight">
                <Container>
                    <div className="rd-newsletter">
                        <div>
                            <h2>{offer ? offerHeadline(offer) : 'Stay in the loop'}</h2>
                            <p>{offer ? 'Join our email list for new arrivals and deals, and we\'ll send your code.' : 'New arrivals, deals and retro news in your inbox.'}</p>
                        </div>
                        <form onSubmit={handleNewsletterSubmit} className="rd-newsletter__form">
                            <label htmlFor="rd-newsletter-email" className="visually-hidden">Email address</label>
                            <input
                                id="rd-newsletter-email"
                                type="email"
                                placeholder="Enter your email"
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                disabled={isSubmitting}
                            />
                            <button type="submit" className="rd-btn rd-btn--primary" disabled={isSubmitting}>
                                {isSubmitting ? 'Sending...' : offer ? 'Send My Code' : 'Subscribe'}
                            </button>
                        </form>
                        <a
                            href="https://uk.trustpilot.com/review/retrodayz.co.uk"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rd-newsletter__reviews"
                        >
                            Read our reviews on Trustpilot &rarr;
                        </a>
                    </div>
                </Container>
            </section>
        </>
    );
};

export default LandingPage;